// frontend/components/POS/ProductGrid.jsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../../services/api';
import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

export default function ProductGrid({ onAddToCart }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, products, services

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['products', search, category, filterType],
    queryFn: () => productService.getProducts({ search, category, filterType, limit: 100 }),
    refetchInterval: 30000
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const cats = await productService.getCategories();
      setCategories(cats || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const handleProductClick = (product) => {
    // Services don't have stock - always available
    if (product.product_type === 'service') {
      onAddToCart({
        id: product.id,
        name: product.name,
        sku: product.sku,
        price: product.unit_price,
        tax_rate: product.tax_rate || 16,
        quantity: 1,
        product_type: product.product_type,
        unit_type: product.unit_type
      });
      return;
    }
    
    // Products need stock check
    if (product.current_stock === 0) {
      toast.error(`${product.name} is out of stock`);
      return;
    }
    
    onAddToCart({
      id: product.id,
      name: product.name,
      sku: product.sku,
      price: product.unit_price,
      tax_rate: product.tax_rate || 16,
      quantity: 1,
      product_type: product.product_type,
      unit_type: product.unit_type
    });
  };

  const clearSearch = () => {
    setSearch('');
    setCategory('');
    setFilterType('all');
  };

  // Calculate tax-inclusive price for display
  const getInclusivePrice = (unitPrice, taxRate) => {
    return unitPrice * (1 + (taxRate || 16) / 100);
  };

  // Get unit type label
  const getUnitTypeLabel = (unitType) => {
    switch(unitType) {
      case 'hour': return '/hr';
      case 'day': return '/day';
      case 'month': return '/month';
      default: return '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 sm:h-96">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search and Filters - Sticky Header */}
      <div className="sticky top-0 bg-gray-100 z-10 pb-3 space-y-2">
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search products or services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-10 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Type Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`flex-1 px-2 py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
              filterType === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Items
          </button>
          <button
            onClick={() => setFilterType('products')}
            className={`flex-1 px-2 py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
              filterType === 'products'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setFilterType('services')}
            className={`flex-1 px-2 py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
              filterType === 'services'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Services
          </button>
        </div>

        {/* Filter Toggle for Mobile */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 w-full"
          >
            <FunnelIcon className="h-4 w-4" />
            {showFilters ? 'Hide Categories' : 'Show Categories'}
            {category && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">1</span>}
          </button>
          
          {/* Active Filters Display */}
          {(search || category || filterType !== 'all') && (
            <button
              onClick={clearSearch}
              className="text-xs text-red-600 hover:text-red-700 whitespace-nowrap"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Categories - Desktop always visible, Mobile conditionally */}
        <div className={`${showFilters || !isMobile ? 'block' : 'hidden'} md:block`}>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <button
              onClick={() => setCategory('')}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-full transition-colors ${
                category === ''
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Categories
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-full transition-colors whitespace-nowrap ${
                  category === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid - Responsive columns */}
      <div className="flex-1 overflow-y-auto mt-3 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
          {data?.data?.map(product => {
            const inclusivePrice = getInclusivePrice(product.unit_price, product.tax_rate);
            const isService = product.product_type === 'service';
            const isOutOfStock = !isService && product.current_stock === 0;
            
            return (
              <button
                key={product.id}
                onClick={() => handleProductClick(product)}
                disabled={isOutOfStock}
                className={`
                  relative p-2 sm:p-3 border rounded-lg text-left transition-all duration-200
                  ${isOutOfStock 
                    ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                    : 'bg-white hover:shadow-md hover:scale-[1.02] active:scale-[0.98]'
                  }
                `}
              >
                {/* Type Badge */}
                {isService ? (
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-purple-100 text-purple-800 text-[9px] sm:text-[10px] font-medium rounded z-10">
                    Ser
                  </span>
                ) : (
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9px] sm:text-[10px] font-medium rounded z-10">
                    Pro
                  </span>
                )}
                
                {/* Stock Badge (only for products) */}
                {!isService && product.current_stock <= product.min_stock_level && product.current_stock > 0 && (
                  <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-[9px] sm:text-[10px] font-medium rounded z-10">
                    Low
                  </span>
                )}
                {isOutOfStock && (
                  <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-red-100 text-red-800 text-[9px] sm:text-[10px] font-medium rounded z-10">
                    Out
                  </span>
                )}
                
                {/* Product Name - Fixed for laptop screens */}
                <div className="font-semibold text-xs sm:text-sm pr-14 pt-4 line-clamp-2 min-h-[2.5rem] sm:min-h-[2.75rem] leading-tight">
                  {product.name}
                </div>
                
                {/* SKU - Hidden on smaller laptops to save space */}
                <div className="hidden lg:block text-[9px] sm:text-[10px] text-gray-400 mt-0.5 truncate">
                  SKU: {product.sku}
                </div>
                
                {/* Unit Type for Services */}
                {isService && product.unit_type && product.unit_type !== 'piece' && (
                  <div className="text-[9px] text-purple-500 mt-0.5">
                    {getUnitTypeLabel(product.unit_type)}
                  </div>
                )}
                
                {/* Price - Prominent */}
                <div className="text-sm sm:text-base md:text-lg font-bold text-blue-600 mt-1 sm:mt-1.5">
                  KES {inclusivePrice.toLocaleString()}
                  {isService && product.unit_type === 'hour' && <span className="text-[9px] font-normal">/hr</span>}
                  {isService && product.unit_type === 'day' && <span className="text-[9px] font-normal">/day</span>}
                  {isService && product.unit_type === 'month' && <span className="text-[9px] font-normal">/month</span>}
                </div>
                
                {/* Tax info combined into single smaller line */}
                <div className="text-[8px] sm:text-[9px] text-gray-400 mt-0.5 truncate">
                  {product.tax_rate || 16}% VAT
                </div>
                
                {/* Stock info (only for products) - Smaller */}
                {!isService && (
                  <div className={`text-[8px] sm:text-[9px] mt-0.5 ${
                    product.current_stock <= product.min_stock_level ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    Stock: {product.current_stock}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* No Results Message */}
        {data?.data?.length === 0 && !isLoading && (
          <div className="text-center py-12 sm:py-16 text-gray-500">
            <p className="text-base sm:text-lg">No items found</p>
            <p className="text-xs sm:text-sm mt-1">Try adjusting your search or category filter</p>
            <button
              onClick={clearSearch}
              className="mt-3 px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}